import { forwardRef, HTMLAttributes, ReactNode, useId } from "react";
import classNames from "classnames/bind";
import { Button } from "app/components/ui/Button/Button";
import {
	Card,
	CardBody,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardMedia,
	CardOrientation,
	CardTitle,
	CardTitleProps,
} from "app/components/ui/Card/Card";
import {
	Carousel,
	CarouselContent,
	CarouselDots,
	CarouselItem,
	CarouselProps,
} from "app/components/ui/Carousel/Carousel";
import { Image } from "app/components/ui/Image/Image";
import Typography from "app/pages/.shared/Typography/Typography";
import { HotelName } from "../HotelName/HotelName";
import styles from "./HotelCard.module.scss";

const cx = classNames.bind(styles);

const DEFAULT_PHOTO_SIZES: Record<CardOrientation, string> = {
	vertical: "(min-width: 1024px) 392px, (min-width: 768px) 50vw, 100vw",
	horizontal: "260px",
};
const PHOTO_RATIOS: Record<CardOrientation, { width: number; height: number }> = {
	vertical: { width: 402, height: 226 },
	horizontal: { width: 260, height: 387 },
};
const CAROUSEL_OPTIONS: Record<CardOrientation, CarouselProps["opts"]> = {
	vertical: undefined,
	horizontal: { duration: 15 },
};

export type HotelCardPhoto = { url: string; thumbnail?: string };

export type HotelCardProps = {
	/** Hotel name, rendered in the heading. */
	name: string;
	/** Hotel stars, shown after the name. Hidden at 0. */
	category?: number;
	/** Level of the heading that holds the name. Defaults to 3. */
	headingLevel?: CardTitleProps["headingLevel"];
	/** Fired by the call to action and by a click on the photo. */
	onSelect: () => void;
	/** Label of the call to action. */
	ctaLabel: ReactNode;
	photos?: HotelCardPhoto[];
	/** Accessible name of the photo carousel. */
	photosLabel: string;
	/** `sizes` attribute of the photos: tells the browser the display width to pick the image file. Defaults to the width of the photo slot for the card orientation. */
	photoSizes?: string;
	/** Badges laid over the top of the photo. Not shown without photos. */
	badges?: ReactNode;
	/** Rating block, resolved by the caller. */
	rating?: ReactNode;
	location?: ReactNode;
	/** Stay duration, already formatted (e.g. "4 nights"). */
	duration?: ReactNode;
	/** Content between the header and the flight, built with `HotelCardDetailList` and `HotelCardDetail`, or one `HotelCardDetailGroup` per room: what the price includes (room, board, perks), or how the rooms are made up. */
	details?: ReactNode;
	/** Flight and baggage summary, resolved by the caller. */
	flight?: ReactNode;
	/** Price block, resolved by the caller. */
	price?: ReactNode;
	/** Highlights the card with a colored banner holding this label, laid over the top of the photo. */
	spotlightLabel?: ReactNode;
	orientation?: CardOrientation;
	/**
	 * Lays the photo, the content and the footer on 3 rows of the parent, so the footers line up
	 * with the neighboring cards. The parent must be `display: grid; grid-template-rows: subgrid`
	 * and span 3 rows.
	 */
	subgrid?: boolean;
} & Omit<HTMLAttributes<HTMLElement>, "children" | "title">;

export const HotelCard = ({
	name,
	orientation = "vertical",
	category,
	headingLevel,
	onSelect,
	ctaLabel,
	photos = [],
	photosLabel,
	photoSizes = DEFAULT_PHOTO_SIZES[orientation],
	badges,
	rating,
	location,
	duration,
	details,
	flight,
	price,
	spotlightLabel,
	subgrid = false,
	className,
	...rest
}: HotelCardProps) => {
	const titleId = useId();
	const isSpotlight = Boolean(spotlightLabel);
	const hasPhotos = photos.length > 0;

	const renderPhoto = (photo: HotelCardPhoto) => (
		<button
			type="button"
			tabIndex={-1}
			aria-hidden="true"
			onClick={onSelect}
			className={cx("hotel-card__photo")}
		>
			<Image
				fill
				src={photo.url}
				thumbnail={photo.thumbnail}
				alt=""
				width={PHOTO_RATIOS[orientation].width}
				height={PHOTO_RATIOS[orientation].height}
				sizes={photoSizes}
			/>
		</button>
	);

	return (
		<Card asChild variant="elevated" orientation={orientation} padding="lg" gap="lg">
			<article
				aria-labelledby={titleId}
				data-orientation={orientation}
				data-spotlight={isSpotlight}
				data-subgrid={subgrid}
				data-testid="hotel-card"
				className={cx("hotel-card", className)}
				{...rest}
			>
				{isSpotlight && (
					<Typography
						component="p"
						variant="regular"
						isBold
						className={cx("hotel-card__spotlight")}
					>
						{spotlightLabel}
					</Typography>
				)}

				{(hasPhotos || subgrid || isSpotlight) && (
					<CardMedia className={cx("hotel-card__media")}>
						{photos.length > 1 && (
							<Carousel
								aria-label={photosLabel}
								fill={orientation === "horizontal"}
								opts={CAROUSEL_OPTIONS[orientation]}
								className={cx("hotel-card__carousel")}
							>
								<CarouselContent arrows="overlay-hover">
									{photos.map((photo, index) => (
										<CarouselItem key={index}>
											{renderPhoto(photo)}
										</CarouselItem>
									))}
								</CarouselContent>

								<CarouselDots className={cx("hotel-card__dots")} />
							</Carousel>
						)}

						{photos.length === 1 && renderPhoto(photos[0])}

						{hasPhotos && badges && (
							<div className={cx("hotel-card__badges")}>{badges}</div>
						)}
					</CardMedia>
				)}

				<CardBody className={cx("hotel-card__body")}>
					<div className={cx("hotel-card__main")}>
						<CardHeader className={cx("hotel-card__header")}>
							<CardTitle
								id={titleId}
								headingLevel={headingLevel}
								variant="xl"
								data-testid="hotel-card-name"
							>
								<HotelName name={name} category={category} />
							</CardTitle>

							{rating}

							{(location || duration) && (
								<div>
									{location && (
										<CardDescription
											className={cx("hotel-card__meta")}
											data-testid="hotel-card-location"
										>
											{location}
										</CardDescription>
									)}

									{duration && (
										<CardDescription
											className={cx("hotel-card__meta")}
											data-testid="hotel-card-duration"
										>
											{duration}
										</CardDescription>
									)}
								</div>
							)}
						</CardHeader>

						{details && (
							<CardContent
								className={cx("hotel-card__details")}
								data-testid="hotel-card-details"
							>
								{details}
							</CardContent>
						)}

						{flight && (
							<CardContent
								className={cx("hotel-card__flight")}
								data-testid="hotel-card-flight"
							>
								{flight}
							</CardContent>
						)}
					</div>

					<CardFooter
						className={cx("hotel-card__footer")}
						data-testid="hotel-card-footer"
					>
						<div className={cx("hotel-card__price")}>{price}</div>

						<Button
							type="button"
							variant="primary"
							onClick={onSelect}
							aria-describedby={titleId}
							data-testid="hotel-card-cta"
						>
							{ctaLabel}
						</Button>
					</CardFooter>
				</CardBody>
			</article>
		</Card>
	);
};

export type HotelCardDetailListProps = HTMLAttributes<HTMLUListElement>;

/** List for the `details` slot of `HotelCard`. */
export const HotelCardDetailList = forwardRef<HTMLUListElement, HotelCardDetailListProps>(
	({ className, ...rest }, ref) => (
		<ul ref={ref} className={cx("hotel-card__detail-list", className)} {...rest} />
	)
);
HotelCardDetailList.displayName = "HotelCardDetailList";

export type HotelCardDetailProps = HTMLAttributes<HTMLLIElement>;

/** One line of the `details` slot (room, board, perk...). Its label is cut to one line. */
export const HotelCardDetail = forwardRef<HTMLLIElement, HotelCardDetailProps>(
	({ className, ...rest }, ref) => (
		<li ref={ref} className={cx("hotel-card__detail", className)} {...rest} />
	)
);
HotelCardDetail.displayName = "HotelCardDetail";

export type HotelCardDetailGroupProps = {
	/** Heading of the group, e.g. "Room 1 (2 adults)". It also names the list. */
	label: ReactNode;
} & HTMLAttributes<HTMLDivElement>;

/** Details of one room among several, set apart under its heading. Holds `HotelCardDetail` items. */
export const HotelCardDetailGroup = forwardRef<HTMLDivElement, HotelCardDetailGroupProps>(
	({ label, className, children, ...rest }, ref) => {
		const labelId = useId();

		return (
			<div ref={ref} className={cx("hotel-card__detail-group", className)} {...rest}>
				<Typography
					id={labelId}
					component="p"
					variant="regular"
					isBold
					className={cx("hotel-card__detail-group-label")}
				>
					{label}
				</Typography>
				<HotelCardDetailList aria-labelledby={labelId}>{children}</HotelCardDetailList>
			</div>
		);
	}
);
HotelCardDetailGroup.displayName = "HotelCardDetailGroup";
